'use client';

import { useEffect, useState } from 'react';

function getBreakpointValue(name: string): string {
  if (typeof window === 'undefined') return '0px';
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(`--breakpoint-${name}`)
      .trim() || '0px'
  );
}

/**
 * Tailwind 4 CSS 변수 기반 브레이크포인트 훅.
 * `--breakpoint-{name}` 값을 읽어 `matchMedia`로 판정.
 *
 * 디자인 시스템(@fastfive-dev/fastfive-design-system)에서 제공하는 브레이크포인트:
 * - `mobile`  (390px)   — 소형 ↔ 일반 모바일 경계
 * - `desktop` (1024.02px) — 모바일 ↔ PC 경계
 *
 * CSS에서는 `mobile:`, `desktop:`, `max-mobile:` 접두사 사용.
 *
 * @example
 * const isDesktop = useBreakpoint('desktop');
 * const isMobile = useBreakpoint('mobile');
 */
export function useBreakpoint(name: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const value = getBreakpointValue(name);
    const mql = window.matchMedia(`(min-width: ${value})`);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [name]);

  return matches;
}
